import type { CommissionSightClient } from '@commissionsight/sdk';
import { Fragment, useCallback, useState } from 'react';
import {
  ErrorNote,
  IconButton,
  type Notify,
  Pager,
  Panel,
  SortHeader,
  btn,
  input,
  timeAgo,
  useRun,
  useSort,
} from '../components/ui.js';
import {
  type UserAccessEntry,
  getUserAccessLog,
  impersonateUser,
  reinviteUser,
} from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';
import { useClientPaged } from '../lib/usePaged.js';

/** A user row + engagement fields the API now returns (ISO strings over JSON).
 *  Typed locally so the admin view reads them without an SDK release. */
interface UserRow {
  id: string;
  email: string;
  role: 'member' | 'admin';
  accountId: string | null;
  accountName: string | null;
  lastLoginAt: string | null;
  loginCount: number;
  lastSeenAt: string | null;
}
const ms = (iso: string | null): number | null => (iso ? Date.parse(iso) : null);

export function Users({ client, notify }: { client: CommissionSightClient; notify: Notify }) {
  const run = useRun(notify);
  const { data, error, reload } = useAsyncData(
    useCallback(async () => {
      const [u, a] = await Promise.all([client.admin.listUsers(), client.admin.listAccounts()]);
      return { users: u.data, accounts: a.data.map((x) => ({ id: x.id, name: x.name })) };
    }, [client]),
  );
  const users = (data?.users ?? []) as unknown as UserRow[];
  const accounts = data?.accounts ?? [];
  const [email, setEmail] = useState('');
  const [accountId, setAccountId] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const sel = 'rounded border border-neutral-200 px-2 py-1 text-xs';
  // Sort by engagement (default: most-recently-active first), then page.
  const { sorted, sortKey, sortDir, toggle } = useSort(users, { key: 'lastSeenAt', dir: 'desc' });
  const paged = useClientPaged(sorted, 20);

  // Per-user access log (expands inline under the row when its icon is clicked).
  const [auditFor, setAuditFor] = useState<string | null>(null);
  const [auditRows, setAuditRows] = useState<UserAccessEntry[]>([]);
  const [auditBusy, setAuditBusy] = useState(false);
  const openAudit = (id: string) => {
    if (auditFor === id) {
      setAuditFor(null);
      return;
    }
    setAuditFor(id);
    setAuditRows([]);
    setAuditBusy(true);
    getUserAccessLog(id)
      .then(setAuditRows)
      .catch((e) => notify(e instanceof Error ? e.message : 'Failed to load access log', 'error'))
      .finally(() => setAuditBusy(false));
  };
  const whenFull = (t: number | string) => new Date(t).toLocaleString('en-US');

  return (
    <Panel title="Users">
      {/* Invite: a user is linked to its account at creation and never moved
          afterwards. Any account may be chosen here (admins may have none). */}
      <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-4">
        <input
          className={input}
          placeholder="email@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <select className={input} value={accountId} onChange={(e) => setAccountId(e.target.value)}>
          <option value="">No account (admin)</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        <select
          className={input}
          value={role}
          onChange={(e) => setRole(e.target.value as 'member' | 'admin')}
        >
          <option value="member">member</option>
          <option value="admin">admin</option>
        </select>
        <button
          type="button"
          className={btn}
          onClick={() =>
            run(
              async () => {
                if (!email) return;
                await client.admin.createUser({ email, accountId: accountId || undefined, role });
                setEmail('');
                setAccountId('');
                setRole('member');
                reload();
              },
              { success: 'User invited' },
            )
          }
        >
          Invite user
        </button>
      </div>
      {error && (
        <div className="mb-3">
          <ErrorNote message={error} onRetry={reload} />
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="text-left text-neutral-500">
          <tr>
            {(() => {
              const sp = { sortKey, sortDir, onSort: toggle };
              return (
                <>
                  <SortHeader label="Email" k="email" {...sp} className="py-1" />
                  <SortHeader label="Account" k="accountName" {...sp} />
                  <SortHeader label="Role" k="role" {...sp} />
                  <SortHeader label="Last active" k="lastSeenAt" {...sp} />
                  <SortHeader label="Logins" k="loginCount" {...sp} />
                  <SortHeader label="Last login" k="lastLoginAt" {...sp} />
                  <th />
                </>
              );
            })()}
          </tr>
        </thead>
        <tbody>
          {paged.pageItems.map((u) => (
            <Fragment key={u.id}>
              <tr className="border-t border-neutral-100">
                <td className="py-2">{u.email}</td>
                {/* Account is fixed at invite — shown read-only (never shifted). */}
                <td className="text-neutral-600">{u.accountName ?? '—'}</td>
                <td>
                  <select
                    className={sel}
                    value={u.role}
                    onChange={(e) => {
                      const next = e.target.value as 'member' | 'admin';
                      if (next === u.role) return;
                      run(
                        async () => {
                          await client.admin.updateUser(u.id, { role: next });
                          reload();
                        },
                        {
                          confirm:
                            next === 'admin'
                              ? `Grant ADMIN access to ${u.email}? They get full back-office control.`
                              : `Revoke admin from ${u.email}?`,
                          success: 'Role updated',
                        },
                      ).then((ok) => {
                        if (!ok) reload(); // revert the select if cancelled/failed
                      });
                    }}
                  >
                    <option value="member">member</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
                <td className="text-neutral-600" title={u.lastSeenAt ?? 'never'}>
                  {timeAgo(ms(u.lastSeenAt))}
                </td>
                <td className="text-neutral-600 tabular-nums">{u.loginCount ?? 0}</td>
                <td className="text-neutral-600" title={u.lastLoginAt ?? 'never'}>
                  {timeAgo(ms(u.lastLoginAt))}
                </td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    {/* Access log (sign-ins with IP / browser / OS + actions). */}
                    <IconButton
                      icon="history"
                      label={`Access log for ${u.email}`}
                      tone={auditFor === u.id ? 'sky' : 'neutral'}
                      onClick={() => openAudit(u.id)}
                    />
                    {/* Re-send the invite email; account-linked users only. */}
                    {u.accountId && (
                      <IconButton
                        icon="mail"
                        label={`Re-invite ${u.email}`}
                        onClick={() =>
                          run(
                            async () => {
                              await reinviteUser(u.id);
                            },
                            {
                              confirm: `Re-send the invite email to ${u.email}?`,
                              success: `Invite re-sent to ${u.email}`,
                            },
                          )
                        }
                      />
                    )}
                    {/* View the app AS this user (read-only support session). */}
                    {u.accountId && (
                      <IconButton
                        icon="view"
                        label={`Impersonate ${u.email}`}
                        onClick={() =>
                          run(
                            async () => {
                              await impersonateUser(u.id);
                            },
                            {
                              confirm: `Open the app as ${u.email}? You'll get a read-only support session in a new tab.`,
                              success: `Opened a read-only session as ${u.email}`,
                            },
                          )
                        }
                      />
                    )}
                    <IconButton
                      icon="remove"
                      label={`Remove ${u.email}`}
                      tone="rose"
                      onClick={() =>
                        run(
                          async () => {
                            await client.admin.deleteUser(u.id);
                            reload();
                          },
                          {
                            confirm: `Remove ${u.email}? This cannot be undone.`,
                            success: 'User removed',
                          },
                        )
                      }
                    />
                  </div>
                </td>
              </tr>
              {auditFor === u.id && (
                <tr className="bg-neutral-50/70">
                  <td colSpan={7} className="px-3 py-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                      Access log — {u.email}
                    </div>
                    {auditBusy ? (
                      <p className="mt-2 text-sm text-neutral-500">Loading…</p>
                    ) : auditRows.length === 0 ? (
                      <p className="mt-2 text-sm text-neutral-500">
                        No access events yet (captured going forward on each sign-in).
                      </p>
                    ) : (
                      <table className="mt-2 w-full text-xs">
                        <thead className="text-left text-neutral-400">
                          <tr>
                            <th className="py-1 font-medium">When</th>
                            <th className="font-medium">Event</th>
                            <th className="font-medium">IP</th>
                            <th className="font-medium">Browser</th>
                            <th className="font-medium">OS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditRows.map((a) => (
                            <tr key={a.id} className="border-t border-neutral-100">
                              <td className="py-1 text-neutral-600">{whenFull(a.createdAt)}</td>
                              <td className="font-mono text-neutral-700">
                                {a.action === 'user.login' ? 'sign-in' : a.action}
                              </td>
                              <td className="font-mono text-neutral-600">{a.ip ?? '—'}</td>
                              <td className="text-neutral-600">{a.meta?.browser ?? '—'}</td>
                              <td className="text-neutral-600">{a.meta?.os ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      {users.length === 0 && !error && <p className="text-sm text-neutral-500">No users.</p>}
      <Pager
        offset={paged.offset}
        count={paged.count}
        total={paged.total}
        hasMore={paged.hasMore}
        onPrev={paged.prev}
        onNext={paged.next}
      />
    </Panel>
  );
}
